import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WorkflowPresets } from '@selfai-platform/pipeline-common';
import { AlertService, DestroyService } from '@selfai-platform/shared';
import { DialogService } from '@selfai-platform/shell';
import { WorkflowStateService } from '@selfai-platform/storage';
import { FieldsetModule } from 'primeng/fieldset';
import { catchError, map, Observable, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { WorkflowPresetsDomainService } from '../../../workflow-presets/services/workflow-presets-domain.service';
import { WorkflowSettingsMetadataComponent } from '../../components/workflow-settings-metadata/workflow-settings-metadata.component';
import { WorkflowSettingsPresetsComponent } from '../../components/workflow-settings-presets/workflow-settings-presets.component';
import { WorkflowSettingsSchedulesComponent } from '../../components/workflow-settings-schedules/workflow-settings-schedules.component';
import { WorkflowSettingsPresetsBinding } from '../../interface';
import { WorkflowSettingsSchedules } from '../../interface/workflow-settings-schedules.interface';
import { WorkflowSettingUpdateInfo } from '../../models';
import { WorkflowSettingsDomainService } from '../../service';
import { WorkflowSettingsPresetsBindingDomainService } from '../../service/workflow-settings-presets-binding-domain.service';
import { WorkflowSettingsPresetsDomainService } from '../../service/workflow-settings-presets-domain.service';
import { WorkflowSettingsSchedulesDomainService } from '../../service/workflow-settings-schedules-domain.service';

@Component({
  selector: 'selfai-platform-workflow-settings-container',
  imports: [
    CommonModule,
    FieldsetModule,
    TranslateModule,
    WorkflowSettingsSchedulesComponent,
    WorkflowSettingsPresetsComponent,
    WorkflowSettingsMetadataComponent,
  ],
  templateUrl: './workflow-settings-container.component.html',
  styleUrl: './workflow-settings-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    WorkflowSettingsPresetsDomainService,
    WorkflowSettingsPresetsBindingDomainService,
    WorkflowSettingsSchedulesDomainService,
    WorkflowSettingsDomainService,
    DestroyService,
  ],
})
export class WorkflowSettingsContainerComponent implements OnInit {
  workflowId = this.activatedRoute.snapshot.params['id'];
  currentPreset$: Observable<WorkflowPresets> = this.workflowSettingsPresetsDomainService.getItem(this.workflowId);

  constructor(
    public workflowPresetsDomainService: WorkflowPresetsDomainService,
    public workflowSettingsPresetsDomainService: WorkflowSettingsPresetsDomainService,
    public workflowSettingsSchedulesDomainService: WorkflowSettingsSchedulesDomainService,
    public workflowSettingsPresetsBindingDomainService: WorkflowSettingsPresetsBindingDomainService,
    private readonly workflowSettingsDomainService: WorkflowSettingsDomainService,
    private activatedRoute: ActivatedRoute,
    public dialogService: DialogService<any>,
    private readonly notificationService: AlertService,
    private readonly translateService: TranslateService,
    private readonly workflowStateService: WorkflowStateService,
    private readonly destroy$: DestroyService,
  ) {}

  ngOnInit(): void {
    this.workflowPresetsDomainService.getList();
    this.workflowSettingsSchedulesDomainService.getList(this.workflowId);
  }

  setPreset(data: WorkflowSettingsPresetsBinding): void {
    this.workflowSettingsPresetsBindingDomainService
      .updatePreset(data)
      .pipe(
        take(1),
        tap(() =>
          this.notificationService.success(
            this.translateService.instant('msg.workspace.settings.presets.update-success'),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onAddSchedule(data: WorkflowSettingsSchedules): void {
    this.workflowSettingsSchedulesDomainService
      .add(data)
      .pipe(
        take(1),
        tap(() =>
          this.notificationService.success(
            this.translateService.instant('msg.workspace.settings.schedules.add-success'),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onEditSchedule(data: WorkflowSettingsSchedules): void {
    this.workflowSettingsSchedulesDomainService.update(data);
  }

  onDeleteSchedule(id: string): void {
    this.workflowSettingsSchedulesDomainService.delete(id);
  }

  editMetadata(data: WorkflowSettingUpdateInfo): void {
    this.workflowSettingsDomainService
      .update(this.workflowId, data)
      .pipe(
        take(1),
        tap(() =>
          this.notificationService.success(
            this.translateService.instant('msg.workspace.settings.metadata.modal.update-success'),
          ),
        ),
        switchMap(() => this.workflowStateService.getWorkflowState()),
        take(1),
        map((workflow) => {
          workflow.thirdPartyData.gui.name = data.name;
          workflow.thirdPartyData.gui.description = data.description;
          workflow.thirdPartyData.gui.isPublic = data.isPublic;

          return workflow;
        }),
        tap((workflow) => this.workflowStateService.setWorkflowState(workflow)),
        catchError((error) => {
          this.notificationService.error(error.message);

          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
